import { z } from 'zod';
/**
 * Прогон агента — одна попытка ответить клиенту.
 *
 * Запись создаётся на каждое входящее сообщение, которое дошло до модели,
 * и живёт независимо от того, чем всё кончилось. Это не журнал отладки:
 * без неё на вопрос «почему клиенту не ответили» отвечать нечем, а он
 * будет задаваться каждый день.
 *
 * Одна строка на прогон, а шаги внутри — в JSONB. Разнести шаги в
 * отдельную таблицу было бы правильнее для запросов вида «сколько
 * вызовов инструмента упало за неделю», но шаги читаются только вместе
 * с прогоном и только человеком, который разбирает конкретный случай.
 * Когда понадобится считать по шагам, таблица появится; заводить её
 * заранее — платить сложностью за отчёт, которого пока никто не просил.
 */
export const AGENT_RUN_STATUSES = [
    'running',
    'answered',
    'skipped',
    'rejected',
    'failed',
];
export const agentRunStatusSchema = z.enum(AGENT_RUN_STATUSES);
export const AGENT_RUN_STATUS_LABELS = {
    running: 'Выполняется',
    answered: 'Ответил',
    skipped: 'Пропущен',
    rejected: 'Не прошёл проверку',
    failed: 'Ошибка',
};
/**
 * Шаг прогона.
 *
 * `model` — обращение к модели, `tool` — вызов инструмента, `gate` —
 * проверка ответа перед отправкой. Три вида, потому что разбираются они
 * по-разному: у обращения к модели важны токены и идентификатор запроса,
 * у инструмента — имя и аргументы, у проверки — вердикт и причина.
 */
export const agentRunStepSchema = z.object({
    index: z.number().int().nonnegative(),
    kind: z.enum(['model', 'tool', 'gate']),
    /** Имя инструмента, модели или проверки — смотря какой вид шага. */
    name: z.string(),
    /**
     * Какой моделью сделан вызов, если он был. `null` — шаг обошёлся без
     * модели (детерминированная проверка, вызов инструмента).
     *
     * Дублирует `name` у шагов вида `model` и нужен ради шагов вида
     * `gate`: у них в имени стоит «проверка», а вызов идёт на служебной
     * модели, которая с 24.09.2026 может отличаться от основной. Без
     * этого поля посчитать стоимость прогона нельзя — токены есть,
     * а по какой цене их считать, неизвестно.
     */
    model: z.string().nullable().default(null),
    ok: z.boolean(),
    durationMs: z.number().int().nonnegative(),
    /**
     * Идентификатор запроса у OpenAI. Единственное, по чему их поддержка
     * может найти конкретный вызов, поэтому хранится всегда.
     */
    requestId: z.string().nullable(),
    inputTokens: z.number().int().nonnegative().nullable(),
    outputTokens: z.number().int().nonnegative().nullable(),
    /** Короткое пояснение: аргументы инструмента, причина отказа гейта. */
    detail: z.string(),
});
export const agentRunSchema = z.object({
    id: z.uuid(),
    conversationId: z.uuid(),
    /** Входящее сообщение, на которое отвечали. */
    messageId: z.uuid().nullable(),
    agentVersionId: z.uuid().nullable(),
    status: agentRunStatusSchema,
    model: z.string(),
    iterations: z.number().int().nonnegative(),
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    /**
     * Стоимость в долларах. null — цена модели не настроена, и это честнее
     * нуля: ноль означал бы «бесплатно», а мы просто не знаем. Подробнее —
     * в комментарии к разбору цен на стороне сервера.
     */
    costUsd: z.number().nullable(),
    durationMs: z.number().int().nonnegative(),
    /** Почему прогон закончился так, а не иначе. Пустая строка при успехе. */
    reason: z.string(),
    steps: z.array(agentRunStepSchema),
    createdAt: z.iso.datetime(),
    finishedAt: z.iso.datetime().nullable(),
});
export const agentRunQuerySchema = z.object({
    conversationId: z.uuid().optional(),
    status: agentRunStatusSchema.optional(),
    limit: z.number().int().min(1).max(100).default(50),
    cursor: z.string().optional(),
});
//# sourceMappingURL=agent-runs.js.map